import nluApi from '../../api/nlu'
import axios from 'axios'
import _ from 'lodash'
import uuid from 'uuid'
import isEmpty from 'lodash/isEmpty'
import trim from 'lodash/trim'
import { MachineLearningTestPreviewService } from '../../services/utterance/machine-learning-test-preview.service'
import store from '../index'
import _get from 'lodash/get'

const MLTestPreviewService = new MachineLearningTestPreviewService()

export default {
  namespaced: true,

  state: {
    value: [],
    loading: false,
    autoPredictedUtterancesQueue: [],
    gpt3Recommendations: [] // { synonym: string, id: uuidv4 }[]
  },

  actions: {
    removeFromAutoPredictionQueue ({ commit }, { utterance }) {
      commit('DELETE_UTTERANCE_FROM_PREDICTED_QUEUE', utterance)
    },

    async autoPredictUtteranceEntities ({ commit, dispatch, rootState, rootGetters }, { utterance, appId }) {
      try {
        const testResponse = await dispatch('knowledgebase/test/testMeaning', {
          appId: 'all',
          q: utterance.text
        }, { root: true })
        if (!testResponse) {
          throw new Error('"testMeaning" has failed.')
        }
        const responseEntities = testResponse.result.entities || []
        if (responseEntities.length === 0) {
          console.debug(`No entities where mapped for utterance ${utterance.text}`)
          return
        }
        const {
          predictedMlEntities,
          predictedNotMlEntities
        } = MachineLearningTestPreviewService.cleanUpTestResponseEntities(
          rootState.knowledgebase.entities.allEntities.value,
          responseEntities
        )
        MLTestPreviewService.updatePossibleMlEntities(rootState.knowledgebase.entities.allEntities.value)
        const mlEntities = MLTestPreviewService.generatePreviewTree(predictedMlEntities)
        if (mlEntities.length) {
          commit('ADD_UTTERANCE_TO_PREDICTED_QUEUE', {
            utterance: {
              ...utterance,
              entities: [],
              entityPredictions: [...predictedNotMlEntities, ...mlEntities]
            },
            appId
          })
        }
      } catch (err) {
        console.debug('Was not able to autopredict entities for utterance:', err)
      }
    },

    async updateUtterance ({ commit, dispatch, rootState, rootGetters }, { appId, data, autoPredictEntities = false }) {
      dispatch('removeFromAutoPredictionQueue', { utterance: data })
      try {
        commit('UPDATE_UTTERANCE', {
          id: data.id,
          updatedFields: { loading: true }
        })
        const utterance = await nluApi.updateUtterance({
          appId,
          kbId: rootState.knowledgebase.current,
          utterance: _.omit(data, ['checked', 'loading', 'application', 'kbId', 'versionId', 'publishedVersionId', 'prebuiltModelId'])
        })
        commit('UPDATE_UTTERANCE', {
          id: data.id,
          updatedFields: { ...utterance }
        })
        dispatch('knowledgebase/_updateModelModifiedDate', null, { root: true })

        if (autoPredictEntities && (rootGetters['knowledgebase/statuses/productionTrainStatus'] === 'READY')) {
          await dispatch('autoPredictUtteranceEntities', { utterance, appId })
        }

        return utterance
      } catch (error) {
        dispatch('knowledgebase/notifications/addError',
          { titleLabel: 'store.utterances.updateUtterance.errorNotification.title', error },
          { root: true }
        )
      } finally {
        commit('UPDATE_UTTERANCE', {
          id: data.id,
          updatedFields: { loading: false }
        })
      }
    },

    async deleteUtterance ({ rootGetters, commit, dispatch, rootState }, { id, appId }) {
      dispatch('removeFromAutoPredictionQueue', { utterance: { id } })
      try {
        commit('UPDATE_UTTERANCE', {
          id,
          updatedFields: { loading: true }
        })
        await nluApi.deleteUtterance({
          id,
          appId,
          kbId: rootState.knowledgebase.current
        })
        commit('DELETE_UTTERANCE', { id })
        dispatch('knowledgebase/_updateModelModifiedDate', null, { root: true })
      } catch (error) {
        commit('UPDATE_UTTERANCE', {
          id,
          updatedFields: { loading: false }
        })
        dispatch('knowledgebase/notifications/addError',
          { titleLabel: 'store.utterances.updateUtterance.errorNotification.title', error },
          { root: true }
        )
      }
    },

    async getGpt3Recommendations ({ rootState, state, commit, dispatch }, { phrase, pinnedIds }) {
      try {
        const response = await axios.post('https://em.staging.api.onereach.ai/http/873c0f61-4dfc-4f8a-860e-f574616703c8/gpt3?feature=synonyms', // hardcoded provider link
          {
            phrase: phrase
          }, {
            headers: {
              Authorization: `USER ${_get(store, 'state.auth.token')}`
            }
          }
        )
        const newItems = response.data.synonyms.map(s => ({ synonym: trim(s), id: uuid.v4() }))

        if (isEmpty(pinnedIds)) {
          commit('SET_GPT3_RECOMMENDATIONS', { value: newItems })
        } else {
          const pinnedItems = state.gpt3Recommendations.value.filter(item => pinnedIds.includes(item.id))
          commit('SET_GPT3_RECOMMENDATIONS', { value: [...pinnedItems, ...newItems] })
        }
      } catch (error) {
        dispatch('knowledgebase/notifications/addError',
          {
            titleLabel: 'store.utterances.getGpt3Recommendations.errorNotification.title',
            error
          },
          { root: true }
        )
      }
    },

    async batchCreatePhrases ({ rootState, rootGetters, commit, dispatch }, { utterances }) {
      const response = await nluApi.batchCreatePairs({
        kbId: rootGetters['knowledgebase/getCurrent'].id,
        utterances
      })
      dispatch('knowledgebase/_updateModelModifiedDate', null, { root: true })

      const updatedUtterancesValue = [...response.accepted, ...rootState.knowledgebase.utterances.value]
      commit('SET_UTTERANCES', { value: updatedUtterancesValue })

      if (response.failed.length !== 0) {
        dispatch('knowledgebase/notifications/addError',
          {
            titleLabel: 'store.utterances.batchCreatePhrases.errorNotification.title',
            error: (response.failed.map(uttr => uttr.reason)).join('. ')
          },
          { root: true }
        )
      }
    }
  },

  mutations: {
    DELETE_UTTERANCE (state, { id }) {
      state.value = state.value.filter(u => u.id !== id)
    },

    UPDATE_UTTERANCE (state, { id, updatedFields }) {
      const utteranceIndex = state.value.findIndex(u => u.id === id)
      if (utteranceIndex > -1) {
        state.value = [
          ...state.value.slice(0, utteranceIndex),
          { ...state.value[utteranceIndex], ...updatedFields, id },
          ...state.value.slice(utteranceIndex + 1)
        ]
      }
    },

    SET_UTTERANCES (state, { loading = false, value = [] }) {
      state.value = value
      state.loading = loading
    },

    ADD_UTTERANCE_TO_PREDICTED_QUEUE (state, { utterance, appId }) {
      state.autoPredictedUtterancesQueue = [...state.autoPredictedUtterancesQueue, { utterance, appId }]
    },

    DELETE_UTTERANCE_FROM_PREDICTED_QUEUE (state, { id }) {
      if (state.autoPredictedUtterancesQueue.some(({ utterance }) => utterance.id === id)) {
        state.autoPredictedUtterancesQueue = state.autoPredictedUtterancesQueue.filter(({ utterance }) => utterance.id !== id)
      }
    },

    SET_GPT3_RECOMMENDATIONS (state, value) {
      state.gpt3Recommendations = value
    }
  }
}
